import { useEffect, useMemo } from "react";
import { Button, Hyperlink, fmt, Separator } from "@racoon/ui";
import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import { useGetTruckByIdQuery, useAllTruckStatusesQuery } from "~/shared/api/truck/truck.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { ConnectProto } from "@racoon/api";
import { badge } from "~/shared/helpers";
import { Row } from "./row";
import { IcWhatsapp } from "@racoon/icons";
import { formatPhoneNumber, reformatPhoneNumber, fromTimestamp } from "@racoon/lib";
import { useAssignRouteModal } from "~/features/assign-route/hooks/use-assign-route-modal";
import { useUserCard } from "~/pages/dashboard/users/ui/use-user-card";
import { useInfoPanel } from "~/features/info-panel/model/useInfoPanel";
import { skipToken } from "@reduxjs/toolkit/query";

type Props = {
  id: string;
  setHeader?: (element: React.ReactNode) => void;
};

export function TruckInfoCard({ id, setHeader }: Props) {
  const t = useT();
  const { openAssignRoute } = useAssignRouteModal();
  const { openUser, userCard } = useUserCard();
  // The route stays inside the panel, the same way the route card opens a truck.
  const { open: openInfoPanel } = useInfoPanel();
  const { selectedCompanyId } = useSelectedCompany();
  const parsedId = useMemo(() => {
    if (!id) return undefined;
    try {
      return BigInt(id);
    } catch {
      return undefined;
    }
  }, [id]);

  const {
    data: truckData,
    isLoading: isTruckLoading,
    error: truckError,
  } = useGetTruckByIdQuery(
    parsedId !== undefined && selectedCompanyId
      ? { id: parsedId, ownerOrganizationId: BigInt(selectedCompanyId) }
      : skipToken,
  );

  const { data: allStatuses, isLoading: isStatusesLoading } = useAllTruckStatusesQuery(
    selectedCompanyId ? { ownerOrganizationIds: [BigInt(selectedCompanyId)] } : skipToken,
  );

  const currentStatus = useMemo(() => {
    if (!allStatuses || !truckData) return null;
    return allStatuses.truckStatuses.find((status) => status.truck?.id === truckData.id) || null;
  }, [allStatuses, truckData]);

  // Set header when data is loaded
  useEffect(() => {
    if (truckData && setHeader) {
      const element = (
        <div className="inline-flex items-center rounded-md border border-text-primary px-3 py-1.5 text-base font-bold">
          {truckData.plateNumber}
        </div>
      );
      setHeader(element);
    }
  }, [truckData, setHeader]);

  const etaMinutes = useMemo(() => {
    const planEnd = currentStatus?.currentRoute?.planEndAt;
    if (!planEnd) return null;

    const end = fromTimestamp(planEnd);
    if (!end) return null;

    // If route is finished (fact_end_at exists), ETA is 0
    if (currentStatus?.currentRoute?.factEndAt) return 0;

    const now = Date.now();
    const diffMs = end.getTime() - now;

    // Convert ms → minutes and clamp to 0
    return Math.max(0, Math.round(diffMs / 60_000));
  }, [currentStatus?.currentRoute?.planEndAt, currentStatus?.currentRoute?.factEndAt]);

  if (truckError) {
    return (
      <div className="p-4 text-sm text-red-600">{t(Namespace.Infopanel, "error.loadTruck")}</div>
    );
  }

  const isLoading = isTruckLoading || isStatusesLoading;

  if (isLoading || !truckData) {
    return (
      <div className="p-4 space-y-3">
        <div className="h-8 w-32 rounded-md" />
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="grid grid-cols-2 items-center gap-2">
            <div className="h-4 w-24" />
            <div className="h-4 w-40" />
          </div>
        ))}
        <div className="h-32 w-full" />
        <div className="h-10 w-full" />
      </div>
    );
  }

  const statusBadge = badge(
    t(
      Namespace.Enum,
      ConnectProto.TruckStatusSchema.values[truckData.status ?? 0].name as TranslationKey,
    ),
    ConnectProto.TruckStatusSchema.values[truckData.status ?? 0]
      .name as TranslationKey<Namespace.Enum>,
  );

  const truckModel = truckData.truckType?.model || truckData.model;
  const driver = currentStatus?.currentShift?.driver;
  const driverName = driver?.firstName
    ? `${driver.lastName || ""} ${driver.firstName}`.trim()
    : null;
  const driverPhone = driver?.phoneNumber;

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 pb-4 flex-1 space-y-6">
        <div className="space-y-[14px]">
          <Row label={t(Namespace.Infopanel, "labels.model")}>
            {truckData?.truckType?.manufacturer} {truckModel}
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.type")}>
            <span>{truckData.truckType?.name}</span>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.truckStatus")}>
            <div className="inline-flex items-center gap-2">{statusBadge}</div>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.capacity")}>
            <span>
              {t(Namespace.Routes, "volumeCubicMeters", {
                volume: truckData.truckType?.volume
                  ? Number(truckData.truckType?.volume)
                  : Number(truckData.capacity),
              })}
            </span>
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.driver")}>
            {driverName && driver ? (
              <Hyperlink onOpen={() => openUser(driver)}>{driverName}</Hyperlink>
            ) : (
              driverName || t(Namespace.Infopanel, "empty")
            )}
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.phone")}>
            {driverPhone ? (
              <div className="inline-flex items-center gap-2">
                <IcWhatsapp />
                <a
                  href={`https://wa.me/${reformatPhoneNumber(driverPhone.replace(/\s+/g, ""))}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline"
                >
                  {formatPhoneNumber(driverPhone)}
                </a>
              </div>
            ) : (
              t(Namespace.Infopanel, "empty")
            )}
          </Row>
        </div>
        <Separator />

        <div className="space-y-[14px]">
          <Row label={t(Namespace.Infopanel, "labels.route")}>
            {currentStatus?.currentRoute?.id ? (
              <Hyperlink
                onOpen={() => openInfoPanel("route", String(currentStatus.currentRoute!.id))}
              >
                M{currentStatus.currentRoute.id.toString()}
              </Hyperlink>
            ) : (
              t(Namespace.Infopanel, "empty")
            )}
          </Row>

          <Row label={t(Namespace.Infopanel, "labels.startTime")}>
            {currentStatus?.currentRoute?.planStartAt
              ? fmt.dateTime(fromTimestamp(currentStatus.currentRoute.planStartAt) as Date)
              : t(Namespace.Infopanel, "empty")}
          </Row>

          <Row label={t(Namespace.Infopanel, "labels.eta")}>
            {currentStatus?.currentRoute?.planEndAt ? (
              <>
                {fmt.dateTime(fromTimestamp(currentStatus.currentRoute.planEndAt) as Date)}{" "}
                <span className="text-slate-500">
                  ({etaMinutes ?? "—"} {t(Namespace.Infopanel, "minutesShort")})
                </span>
              </>
            ) : (
              t(Namespace.Infopanel, "empty")
            )}
          </Row>
        </div>
      </div>

      <div className="px-4 pb-4 mt-auto">
        <Button
          id="truck-info-assign-route-button"
          className="w-full"
          variant="opacityBlueWithOutline"
          onClick={() =>
            openAssignRoute({
              preselectedTruckId: truckData?.id ? String(truckData.id) : undefined,
            })
          }
        >
          {t(Namespace.Infopanel, "actions.assignOrder")}
        </Button>
      </div>
      {userCard}
    </div>
  );
}
